
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

interface AdaptiveDifficultyOptions {
  // Minimum number of questions before making an adjustment
  minQuestionsBeforeAdjustment?: number;
  // Threshold for increasing difficulty (percentage correct)
  increaseThreshold?: number;
  // Threshold for decreasing difficulty (percentage correct)
  decreaseThreshold?: number;
  // Whether to show notifications on difficulty change
  showNotifications?: boolean;
  // Number of recent questions to consider
  recentQuestionsWindow?: number;
  // Maximum time (ms) for a "fast" answer
  fastAnswerThresholdMs?: number;
  // Required consecutive correct answers to increase difficulty
  requiredConsecutiveCorrect?: number;
  // Start with medium difficulty
  startWithMedium?: boolean;
  // Score multipliers for different difficulties
  scoreMultipliers?: {
    easy: number;
    medium: number;
    hard: number;
  };
}

// Create a minimal interface for the state properties we actually need
interface AdaptiveState {
  difficulty: "easy" | "medium" | "hard";
  questionIndex?: number;
  subject?: string;
  category?: string;
}

export function useRealTimeAdaptiveDifficultyCA<T extends AdaptiveState>(
  state: T,
  setState: (state: T | ((prev: T) => T)) => void,
  options: AdaptiveDifficultyOptions = {}
) {
  // Default options
  const {
    minQuestionsBeforeAdjustment = 1, 
    increaseThreshold = 0.8,
    decreaseThreshold = 0.4,
    showNotifications = true,
    recentQuestionsWindow = 3, // Shorter window for current affairs
    fastAnswerThresholdMs = 8000, // 8 seconds threshold for current affairs
    requiredConsecutiveCorrect = 2,
    startWithMedium = true,
    scoreMultipliers = { easy: 1, medium: 1.5, hard: 2 } // Different multipliers for current affairs
  } = options;

  // Track correct/incorrect answers
  const [correctAnswers, setCorrectAnswers] = useState<boolean[]>([]);
  const [answerTimes, setAnswerTimes] = useState<number[]>([]);
  const [lastAdjustmentIndex, setLastAdjustmentIndex] = useState(-1);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [consecutiveFastCorrect, setConsecutiveFastCorrect] = useState(0);
  const [consecutiveIncorrect, setConsecutiveIncorrect] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [initialDifficultySet, setInitialDifficultySet] = useState(false);

  // Initialize difficulty to medium right away if startWithMedium is true
  useEffect(() => {
    if (startWithMedium && !initialDifficultySet) {
      console.log("[CA] Setting initial difficulty to medium");
      setState(prev => ({
        ...prev,
        difficulty: "medium"
      }));
      setInitialDifficultySet(true);
    }
  }, [startWithMedium, initialDifficultySet, setState]);

  // Reset tracking when subject or category changes
  useEffect(() => {
    console.log("[CA] Resetting adaptive difficulty tracking due to subject/category change");
    setCorrectAnswers([]);
    setAnswerTimes([]);
    setLastAdjustmentIndex(-1);
    setQuestionStartTime(Date.now());
    setConsecutiveFastCorrect(0);
    setConsecutiveIncorrect(0);
    setCurrentStreak(0);
    setInitialDifficultySet(false);
    
    // Reset difficulty to medium when subject changes if startWithMedium is true
    if (startWithMedium) {
      setState(prev => ({
        ...prev,
        difficulty: "medium"
      }));
      setInitialDifficultySet(true);
      console.log("[CA] Subject/category changed: Reset difficulty to medium");
    }
  }, [state.subject, state.category, startWithMedium, setState]);

  // Set question start time when question index changes
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [state.questionIndex]);

  const recordAnswer = useCallback((isCorrect: boolean, answerTimeMs?: number) => {
    const now = Date.now();
    // Use provided answer time if available, otherwise calculate from start time
    const answerTime = answerTimeMs || (now - questionStartTime);
    const isFast = answerTime < fastAnswerThresholdMs;
    
    console.log(`[CA] Answer recorded: ${isCorrect ? 'Correct' : 'Incorrect'}, Time: ${answerTime}ms, ${isFast ? 'Fast' : 'Slow'}`);
    console.log(`[CA] Current difficulty before evaluation: ${state.difficulty}`);
    
    // Record answer correctness
    setCorrectAnswers(prev => {
      const updated = [...prev, isCorrect];
      // Keep only the most recent window
      return updated.slice(-recentQuestionsWindow);
    });
    
    // Record answer time
    setAnswerTimes(prev => {
      const updated = [...prev, answerTime];
      // Keep only the most recent window
      return updated.slice(-recentQuestionsWindow);
    });

    // Update streak tracking
    if (isCorrect) {
      const newStreak = currentStreak > 0 ? currentStreak + 1 : 1;
      setCurrentStreak(newStreak);
      setConsecutiveIncorrect(0);
      
      console.log(`[CA] Correct answer! Current streak: ${newStreak}`);
      
      // Update consecutive fast correct counter
      if (isFast) {
        const newFastCorrect = consecutiveFastCorrect + 1;
        setConsecutiveFastCorrect(newFastCorrect);
        console.log(`[CA] Fast correct answer! Consecutive count: ${newFastCorrect}`);
      } else {
        // Reset fast counter if correct but slow
        setConsecutiveFastCorrect(0);
        console.log(`[CA] Correct but slow answer. Fast streak reset.`);
      }
    } else {
      const newNegStreak = currentStreak < 0 ? currentStreak - 1 : -1;
      setCurrentStreak(newNegStreak);
      setConsecutiveFastCorrect(0);
      const newIncorrectCount = consecutiveIncorrect + 1;
      setConsecutiveIncorrect(newIncorrectCount);
      console.log(`[CA] Incorrect answer. Consecutive incorrect: ${newIncorrectCount}, Streak: ${newNegStreak}`);
    }
    
    // Update question start time for next question
    setQuestionStartTime(now);

    // Determine score multiplier based on current difficulty
    const scoreMultiplier = scoreMultipliers[state.difficulty] || 1;

    // Apply score adjustment based on difficulty
    if (isCorrect) {
      setState(prev => ({
        ...prev,
        score: (prev as any).score + scoreMultiplier
      }));
    }

    // Calculate performance metrics for potential difficulty adjustment
    evaluateDifficultyAdjustment(isCorrect, isFast);
  }, [questionStartTime, recentQuestionsWindow, fastAnswerThresholdMs, 
      scoreMultipliers, state.difficulty, setState, 
      consecutiveFastCorrect, consecutiveIncorrect, currentStreak]);

  // For Current Affairs: More frequent difficulty changes with simpler rules
  const evaluateDifficultyAdjustment = useCallback((isCorrect: boolean, isFast: boolean) => {
    let shouldAdjust = true;
    let newDifficulty = state.difficulty;
    let adjustmentReason = "";

    // Log the current state for debugging
    console.log('[CA] Evaluating difficulty adjustment:', {
      isCorrect,
      isFast,
      currentDifficulty: state.difficulty,
      consecutiveFastCorrect: isCorrect && isFast ? consecutiveFastCorrect + 1 : consecutiveFastCorrect,
      consecutiveIncorrect: !isCorrect ? consecutiveIncorrect + 1 : consecutiveIncorrect,
      currentStreak,
      questionIndex: state.questionIndex
    });

    // Current Affairs specific rules (more responsive than the regular quiz)
    
    // Case 1: Two consecutive fast and correct answers - increase difficulty
    if (isCorrect && isFast && consecutiveFastCorrect >= 1) {
      if (state.difficulty === "easy") {
        newDifficulty = "medium";
        adjustmentReason = "[CA] two consecutive fast correct answers on easy";
      } else if (state.difficulty === "medium") {
        newDifficulty = "hard";
        adjustmentReason = "[CA] two consecutive fast correct answers on medium";
      }
    } 
    // Case 2: Two consecutive incorrect answers - decrease difficulty
    else if (!isCorrect && consecutiveIncorrect >= 1) {
      if (state.difficulty === "hard") {
        newDifficulty = "medium";
        adjustmentReason = "[CA] two consecutive incorrect answers on hard";
      } else if (state.difficulty === "medium") {
        newDifficulty = "easy";
        adjustmentReason = "[CA] two consecutive incorrect answers on medium";
      }
    }
    // Case 3: Very slow correct answer (showing uncertainty)
    else if (isCorrect && !isFast && answerTimes.length > 0 && answerTimes[answerTimes.length - 1] > fastAnswerThresholdMs * 2) {
      if (state.difficulty === "hard") {
        newDifficulty = "medium";
        adjustmentReason = "[CA] very slow correct answer on hard - showing uncertainty";
      } else if (state.difficulty === "medium" && currentStreak <= 1) {
        newDifficulty = "easy";
        adjustmentReason = "[CA] very slow correct answer on medium with no streak - showing uncertainty";
      }
    }
    
    // If difficulty should change
    if (shouldAdjust && newDifficulty !== state.difficulty) {
      console.log(`[CA] Adjusting difficulty from ${state.difficulty} to ${newDifficulty} due to ${adjustmentReason}`);
      
      setState(prev => ({
        ...prev,
        difficulty: newDifficulty
      }));
      
      setLastAdjustmentIndex(state.questionIndex || 0);
      
      // Reset tracking counters after adjustment based on direction
      if (newDifficulty > state.difficulty) {
        // Reset consecutive fast correct for increased difficulty
        setConsecutiveFastCorrect(0);
      } else {
        // Reset consecutive incorrect for decreased difficulty
        setConsecutiveIncorrect(0);
      }
      
      if (showNotifications) {
        if (newDifficulty > state.difficulty) {
          toast.success(`Difficulty increased to ${newDifficulty} – great job!`);
        } else {
          toast.info(`Taking it slower with ${newDifficulty} difficulty – let's review together.`);
        }
      }
    } else {
      console.log(`[CA] No difficulty change needed. Staying at ${state.difficulty}`);
    }
  }, [state.difficulty, state.questionIndex, setState, 
      showNotifications, answerTimes,
      consecutiveFastCorrect, consecutiveIncorrect, currentStreak]);

  // Return utility functions and current state
  return {
    recordCorrectAnswer: () => recordAnswer(true),
    recordIncorrectAnswer: () => recordAnswer(false),
    recordAnswerWithTime: (isCorrect: boolean, timeMs: number) => recordAnswer(isCorrect, timeMs),
    currentDifficulty: state.difficulty,
    consecutiveFastCorrect,
    consecutiveIncorrect,
    currentStreak,
    scoreMultiplier: scoreMultipliers[state.difficulty] || 1
  };
}
